import React from "react";
import { useReducerWrapper } from "shared/hooks/useReducerWrapper";
import { useGetStaticData } from "./dataloader";
import { StaticDataDispatchInterface, StaticDataObject, StaticDataProviderProps, StaticDataState } from "./staticData";
import Reducer from "./reducer";

const Context = React.createContext<StaticDataObject>({} as StaticDataObject);

function StaticDataProvider(props: StaticDataProviderProps) {
  const [state, dispatch] = useReducerWrapper<StaticDataState, StaticDataDispatchInterface>(Reducer, {
    registrationKey: props.localeId
  });
  const { data } = useGetStaticData({ regKey: state.localeId });

  /**
   * Call this function to refresh static data for particular registration key
   */
  const refetchStaticData = React.useCallback(
    registrationKey => {
      dispatch.changeLanguageType(registrationKey);
    },
    [dispatch]
  );

  if (!data) {
    // todo:rr need to show a loader until loading
    // if error then just show toast msg
    return null;
  }

  const staticData = {
    ...data.platformConstants,
    ...data.registrationConstants,
    staticOptions: data.staticOptions,
    roleAndRegistrationWorkflowTypeCombinationForRegForm: data.roleAndRegistrationWorkflowTypeCombinationForRegForm,
    roleAndSubRoleCombinationForRegForm: data.roleAndSubRoleCombinationForRegForm,
    refetchStaticData,
    copies: data.localI18nMessages || {},
    inventoryTypeRateCardQuantityTypeMap: data.inventoryTypeRateCardQuantityTypeMap,
    showOnTicketsPageRoles: data.showOnTicketsPageRoles,
    divisionApplicableRoles: data.divisionApplicableRoles,
    eligibleForTicketsPage: data.eligibleForTicketsPage,
    applicableRegistrationTransitionState: data.applicableRegistrationTransitionState,
    singleAssignmentInventoryTypes: data.singleAssignmentInventoryTypes,
    couponCodeSupportedInventoryType: data.couponCodeSupportedInventoryType,
    additionalChargeInventoryTypes: data.additionalChargeInventoryTypes,
    rateCardInventoryTypes: data.rateCardInventoryTypes,
    inventoryTypeContextTypeMap: data.inventoryTypeContextTypeMap,
    mandatoryProgramParticipantTagMapping: data.mandatoryProgramParticipantTagMapping,
    separator: { ptSeparator: data.ptSeparator },
    programTypeApplicableTagMap: data.programTypeApplicableTagMap,
    restrictedRenderTypeForEntity: data.restrictedRenderTypeForEntity,
    restrictedRenderTypeForForm: data.restrictedRenderTypeForForm,
    supportedEntityTypesForForm: data.supportedEntityTypesForForm,
    selectableTypeToTemplateTypeMap: data.selectableTypeToTemplateTypeMap,
    programTypeToSelectableTypeMap: data.programTypeToSelectableTypeMap,
    defaultFSLConfigurableForProgramTypes: data.defaultFSLConfigurableForProgramTypes,
    searchableFieldEntityTypes: data.searchableFieldEntityTypes,
    searchableFieldRenderTypes: data.searchableFieldRenderTypes,
    programTypeDefaultHeaderImageMap: data.programTypeDefaultHeaderImageMap,
    noRateCardNeededWorkflowTypes: data.noRateCardNeededWorkflowTypes,
    canHaveSponsorOrganizationsWorkflowTypes: data.canHaveSponsorOrganizationsWorkflowTypes,
    shouldSelectRateCardWorkflowTypes: data.shouldSelectRateCardWorkflowTypes,
    hasNoMaxRegistrationsLimitWorkflowTypes: data.hasNoMaxRegistrationsLimitWorkflowTypes,
    selectableTransactionMethodTypes: data.selectableTransactionMethodTypes,
    vendorSpecificDataMap: data.vendorSpecificDataMap,
    vendorAuthenticationTypes: data.vendorAuthenticationTypes,
    externalFieldRenderTypes: data.externalFieldRenderTypes,
    vendorAdvanceMetaTypes: data.vendorAdvanceMetaTypes,
    tabletalkProfileFormFieldExclusionMap: data.tabletalkProfileFormFieldExclusionMap,
    inventoryTypeVsWorkflowType: data.inventoryTypeVsWorkflowType,
    criteriaConfigTypeVsInventoryTypeMap: data.criteriaConfigTypeVsInventoryTypeMap,
    hideSponsorOrganizationsParticipantRoles: data.hideSponsorOrganizationsParticipantRoles,
    exclusiveAssociatedActivityPriorities: data.exclusiveAssociatedActivityPriorities,
    nonExclusiveAssociatedActivityPriorities: data.nonExclusiveAssociatedActivityPriorities,
    customEventActionDefaultIconImage: data.customEventActionDefaultIconImage,
    bulkUploadSupportedEntities: data.bulkUploadSupportedEntities,
    supportedFormForContextType: data.supportedFormForContextType
  };

  return <Context.Provider value={staticData} {...props} />;
}

/**
 * Returns static data
 */
function useStaticData() {
  const context = React.useContext(Context);
  if (context === undefined) {
    throw new Error(`useStaticContext must be used within a StaticDataProvider`);
  }
  return context;
}

export { StaticDataProvider, useStaticData };
